import { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Wallet, Activity } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const [summary, setSummary] = useState({ total_income: 0, total_expense: 0, balance: 0, transaction_count: 0 });
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [categoryReport, setCategoryReport] = useState([]);
  const [dateReport, setDateReport] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [summaryRes, transactionsRes, categoryRes, dateRes] = await Promise.all([
        axios.get(`${API}/reports/summary`),
        axios.get(`${API}/transactions`),
        axios.get(`${API}/reports/by-category`),
        axios.get(`${API}/reports/by-date?group_by=month`)
      ]);

      setSummary(summaryRes.data);
      setRecentTransactions(transactionsRes.data.slice(0, 5));
      setCategoryReport(categoryRes.data.slice(0, 5));
      setDateReport(dateRes.data.slice(-6));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, type, trend }) => (
    <div className="bg-card border border-border rounded-xl p-6 shadow-sm stat-card-hover" data-testid={`stat-${type}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
          <p className={`text-3xl font-black font-mono mt-2 ${
            type === 'income' ? 'text-income' : type === 'expense' ? 'text-expense' : 'text-primary'
          }`}>
            ${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className={`p-3 rounded-lg ${
          type === 'income' ? 'bg-income/10' : type === 'expense' ? 'bg-expense/10' : 'bg-muted'
        }`}>
          <Icon size={24} className={type === 'income' ? 'text-income' : type === 'expense' ? 'text-expense' : 'text-primary'} strokeWidth={1.5} />
        </div>
      </div>
    </div>
  );

  const COLORS = ['#60A5FA', '#10B981', '#EF4444', '#F59E0B', '#94A3B8'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      <div>
        <h2 className="text-3xl sm:text-4xl font-black font-heading tracking-tight text-primary">Dashboard</h2>
        <p className="text-sm sm:text-base text-muted-foreground mt-2">Overview of your financial situation</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <StatCard title="Total Income" value={summary.total_income} icon={TrendingUp} type="income" />
        <StatCard title="Total Expenses" value={summary.total_expense} icon={TrendingDown} type="expense" />
        <StatCard title="Balance" value={summary.balance} icon={Wallet} type="balance" />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Monthly Trend */}
        <div className="bg-card border border-border rounded-xl p-4 sm:p-6 shadow-sm">
          <h3 className="text-lg sm:text-xl font-bold font-heading tracking-tight text-primary mb-4 sm:mb-6">Monthly Trend</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={dateReport}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="period" stroke="#94A3B8" style={{ fontSize: '12px' }} />
              <YAxis stroke="#94A3B8" style={{ fontSize: '12px' }} />
              <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155' }} />
              <Legend />
              <Line type="monotone" dataKey="income" stroke="#10B981" strokeWidth={2} name="Income" />
              <Line type="monotone" dataKey="expense" stroke="#EF4444" strokeWidth={2} name="Expenses" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Category Distribution */}
        <div className="bg-card border border-border rounded-xl p-4 sm:p-6 shadow-sm">
          <h3 className="text-lg sm:text-xl font-bold font-heading tracking-tight text-primary mb-4 sm:mb-6">Category Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={categoryReport}
                dataKey="total_amount"
                nameKey="category_name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={(entry) => {
                  const name = entry.category_name.length > 15 ? entry.category_name.substring(0, 12) + '...' : entry.category_name;
                  return `${name} (${entry.percentage.toFixed(0)}%)`;
                }}
              >
                {categoryReport.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-card border border-border rounded-xl p-4 sm:p-6 shadow-sm">
        <h3 className="text-lg sm:text-xl font-bold font-heading tracking-tight text-primary mb-4 sm:mb-6">Recent Transactions</h3>
        {recentTransactions.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No transactions yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground uppercase tracking-wider">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground uppercase tracking-wider">Description</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground uppercase tracking-wider">Type</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody>
                {recentTransactions.map((trans, index) => (
                  <tr key={index} className="border-b border-border table-row-hover" data-testid={`recent-transaction-${index}`}>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {new Date(trans.date).toLocaleDateString('en-US')}
                    </td>
                    <td className="py-3 px-4 text-sm text-foreground">{trans.description || '-'}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                        trans.type === 'income' ? 'bg-income/10 text-income' : 'bg-expense/10 text-expense'
                      }`}>
                        {trans.type === 'income' ? 'Income' : 'Expense'}
                      </span>
                    </td>
                    <td className={`py-3 px-4 text-right font-mono font-semibold ${
                      trans.type === 'income' ? 'text-income' : 'text-expense'
                    }`}>
                      ${trans.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}